import {
  Bank,
  Button,
  Callout,
  Environment,
  Modal,
  Selector,
  User,
  onEnvironmentChange,
  onProfileCompletenessChange,
  onUserChange,
} from '@hub/shared'
import PaymentFormNf from '@pages/payments-data/components/form-payment-nf/form-payment-nf.component'
import PaymentFormRpa from '@pages/payments-data/components/form-payment-rpa/form-payment-rpa.component'
import { CanEdit } from '@pages/payments-data/payments-data.page'
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'
import PaymentPayloadHelper from '../../helper/payment-payload.helper'
import './payment-content-page.component.scoped.scss'
import './payment-content-page.component.scss'

interface PaymentPageProps {
  payload: Bank | null
  seeData: boolean
  hideData: boolean
  loading: boolean
  canEdit?: CanEdit
  onSave?: (payload: Bank | null) => Promise<void>
  onSeeData?: (value: boolean) => void
}

export default ({ payload, canEdit, hideData, onSave, onSeeData, loading }: PaymentPageProps) => {
  const [t] = useTranslation('paymentsData')
  const [loadingButton, setLoadingButton] = useState<boolean>(loading)
  const [percentage, setPercentage] = useState<boolean>(false)
  const [user, setUser] = useState<User | null>(null)
  const [paymentData, setPaymentData] = useState<Bank | null>(null)
  const [paymentType, setPaymentType] = useState<string>('rpa')
  const [urlCreators, setUrlCreators] = useState<string>('')
  const onProfileChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const onEnvironmentChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const onUserChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const [openModal, setOpenModal] = useState<boolean>(false)
  const paymentPayloadHelper = useMemo(() => new PaymentPayloadHelper(), [])

  const [validSubmit, setValidSubmit] = useState({
    rpa: false,
    nf: false,
  })

  const handleCheckValidSubmit = useCallback((): boolean => {
    if (paymentData?.paymentType === 'rpa') {
      return validSubmit.rpa
    }
    return validSubmit.nf
  }, [paymentData, validSubmit])

  const handleOnSubmit = useCallback(
    (event: FormEvent) => {
      event?.preventDefault()
      if (onSave && typeof onSave === 'function' && handleCheckValidSubmit()) {
        setLoadingButton(true)
        onSave(paymentData)
      }
    },
    [onSave, paymentData, handleCheckValidSubmit]
  )

  const handleOnSeeData = useCallback(
    (value: boolean) => {
      if (onSeeData && typeof onSeeData === 'function') {
        onSeeData(value)
      }
    },
    [onSeeData]
  )

  const handleOnChange = (fieldValue: string) => {
    if (paymentData?.id && fieldValue !== paymentData.paymentType) {
      setOpenModal(true)
    } else {
      changeValue(fieldValue)
    }
  }

  const changeValue = (fieldValue: string) => {
    setPaymentType(fieldValue)

    const clearPayments = paymentPayloadHelper.clearReceiveBank(paymentData, fieldValue) ?? null
    setPaymentData(clearPayments)

    if (paymentData?.holderDocument && fieldValue === 'rpa' && paymentData?.holderDocument !== user?.profileadditionalinfo?.document) {
      paymentData.holderDocument = user?.profileadditionalinfo?.document || ''
    }
  }

  useEffect(() => {
    onUserChangeSubscription.current = onUserChange.subscribe((user: User) => {
      if (user?.squidId) {
        setUser(user)
      }
    })

    onProfileChangeSubscription.current = onProfileCompletenessChange.subscribe((completeness) => {
      if (completeness) {
        setPercentage(completeness?.paymentData?.percentage >= 100)
      }
    })
    onEnvironmentChangeSubscription.current = onEnvironmentChange.subscribe((environments: Environment) => {
      setUrlCreators(environments?.squidProducts?.creators)
    })

    return () => {
      onUserChangeSubscription?.current?.unsubscribe()
      onProfileChangeSubscription?.current?.unsubscribe()
      onEnvironmentChangeSubscription?.current?.unsubscribe()
    }
  }, [])

  useEffect(() => {
    setPaymentData(payload)
    setPaymentType(payload?.paymentType || 'rpa')
  }, [payload])

  useEffect(() => {
    setLoadingButton(loading)
  }, [loading])

  return (
    <div>
      <form onSubmit={(event: FormEvent<Element>) => handleOnSubmit(event)}>
        {!percentage ? (
          <Callout className="mt-0 mb-5" title={t('warning.title')}>
            <span dangerouslySetInnerHTML={{ __html: t('warning.message') }}></span>
          </Callout>
        ) : null}
        <h5 className="mb-5">{t('personalData.title')}</h5>
        <div className="display-flex justify-content-space-between mb-2">
          <p style={{ fontWeight: 500 }}>{t('personalData.select')}</p>
          {user?.profileadditionalinfo?.documentType !== 'CNPJ' ? (
            <p>
              <i className="fa-solid fa-circle-exclamation mr-2" style={{ color: 'var(--text_color)' }}></i>
              <a target="_blank" href={`${urlCreators}/docs/o-que-e-mais-vantajoso-cnpj-ou-rpa`}>
                {t('knowMore')}
              </a>
            </p>
          ) : null}
        </div>
        <div className="checkbox-tabs">
          {user?.profileadditionalinfo?.documentType !== 'CNPJ' ? (
            <Selector
              name={'radio-payment-type-rpa'}
              id={'radio-payment-type'}
              className="radio-payment-type"
              checked={paymentType === 'rpa'}
              onChange={(_checked: boolean) => handleOnChange('rpa')}
              rightLabel={<span className={`radio-label ${paymentType === 'rpa' ? 'active' : ''}`}>{t('personalData.pf')}</span>}
              type={'radio'}
              hideInput
              errorSpan={false}
              readOnly={!canEdit?.bank || !canEdit?.full}
            />
          ) : null}
          <Selector
            name={'radio-payment-type-nf'}
            id={'radio-payment-type-nf'}
            className="radio-payment-type"
            checked={paymentType === 'nf'}
            onChange={(checked: boolean) => handleOnChange('nf')}
            rightLabel={<div className={`radio-label ${paymentType === 'nf' ? 'active' : ''}`}>{t('personalData.pj')}</div>}
            type={'radio'}
            hideInput
            errorSpan={false}
            readOnly={!canEdit?.bank || !canEdit?.full}
          />
        </div>
        {paymentData?.paymentType !== 'rpa' || user?.profileadditionalinfo?.documentType === 'CNPJ' ? (
          <PaymentFormNf
            payload={paymentData}
            initialPayload={payload}
            canEdit={canEdit}
            hideData={hideData}
            onPayloadChange={(value) => setPaymentData(value)}
            onValidate={(value) => setValidSubmit({ ...validSubmit, nf: value })}
          />
        ) : (
          <PaymentFormRpa
            payload={paymentData}
            initialPayload={payload}
            canEdit={canEdit}
            hideData={hideData}
            onPayloadChange={(value) => setPaymentData(value)}
            onValidate={(value) => setValidSubmit({ ...validSubmit, rpa: value })}
          />
        )}
        <footer className="mt-5">
          <Button
            onClick={() => handleOnSeeData(false)}
            size="md"
            disabled={loadingButton}
            color="transparent"
            textColor="var(--text_color)"
            borderStyle="none"
          >
            {t('cancel')}
          </Button>
          <Button size="md" loading={loadingButton} disabled={!handleCheckValidSubmit() || loadingButton || hideData} type="submit">
            {t('save')}
          </Button>
        </footer>
      </form>
      <Modal
        open={openModal}
        onCloseChange={() => {
          setOpenModal(false)
        }}
        header={<h5 className="mb-0">{t('modal.changePaymentData')}</h5>}
        footer={
          <div className="menu-modal col-24 display-flex justify-content-space-between">
            <Button
              onClick={() => {
                setOpenModal(false)
              }}
              id="cancel-button-navigation-modal"
              color="transparent"
              textColor="var(--text_color)"
              borderStyle="none"
            >
              <span className="text-button" style={{ whiteSpace: 'nowrap', display: 'inline-block', width: '100%' }}>
                {t('modal.cancel')}
              </span>
            </Button>
            <Button
              onClick={() => {
                changeValue(paymentData?.paymentType === 'nf' ? 'rpa' : 'nf')
                setOpenModal(false)
              }}
              id="confirm-button-navigation-modal"
              className="confirm-button-navigation"
            >
              <span className="text-button" style={{ whiteSpace: 'nowrap', display: 'inline-block', width: '100%' }}>
                {t('modal.confirm')}
              </span>
            </Button>
          </div>
        }
      >
        <Callout title={t('modal.caution')}>{t('modal.warningChangePaymentData')}</Callout>
      </Modal>
    </div>
  )
}
